import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  lessonTaskApi,
  tPromiseFromFunctionResolved,
  ITaskSession,
  ITaskSessionState,
  tAnswer,
  tSharedAppState,
  tSharedAppDispatch
} from 'common/modules/ReactShared';
import { AxiosError } from 'axios';
import { actions as rewardNotificationsActions } from '../../member/rewardNotifications/slice';
import lessonTaskSelectors from '../selectors';
import { isPreviewingLesson } from '../../lesson/preview';

type tLessonTaskSession = ITaskSessionState | null;
const initialLessonTaskSessionState: tLessonTaskSession = null;

const fetchLessonTaskSession = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof lessonTaskApi.fetchTaskSession>,
  { lessonId: number; taskId: number },
  { state: tSharedAppState }
>(
  'lessonTaskSession/fetchSession_request',
  async ({ lessonId, taskId }, { getState }) => {
    const { taskSession, leaderBoard } = await lessonTaskApi.fetchTaskSession(
      lessonId,
      taskId,
      isPreviewingLesson(getState(), lessonId)
    );

    return { taskSession, leaderBoard };
  }
);

const restartLessonTaskSession = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof lessonTaskApi.restartTaskSession>,
  { lessonSessionId: number; taskSessionId: number }
>(
  'lessonTaskSession/restartSession_request',
  async ({ lessonSessionId, taskSessionId }) => {
    const { taskSession, leaderBoard } = await lessonTaskApi.restartTaskSession(
      lessonSessionId,
      taskSessionId
    );

    return { taskSession, leaderBoard };
  }
);

const submitAnswersLessonTask = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof lessonTaskApi.submitAnswersLessonTask> | null,
  {
    taskSession: ITaskSession;
    answers: tAnswer;
  },
  { dispatch: tSharedAppDispatch }
>('lessonTaskSession/submitAnswersLessonTask_request', ({ taskSession, answers }) => {
  if (taskSession && answers) {
    return lessonTaskApi.submitAnswersLessonTask(taskSession.url, answers);
  }

  return null;
});

const submitAnswersLessonTaskItem = createAsyncThunk<
  tPromiseFromFunctionResolved<typeof lessonTaskApi.submitAnswersLessonTaskItem> | null,
  tAnswer[number],
  { dispatch: tSharedAppDispatch; state: tSharedAppState }
>(
  'lessonTaskSession/submitAnswersLessonTaskItem_request',
  async (taskItemSession, { getState }) => {
    if (taskItemSession.url) {
      return lessonTaskApi.submitAnswersLessonTaskItem(
        taskItemSession.url,
        taskItemSession
      );
    }

    const taskSession = lessonTaskSelectors.selectLessonTaskSession(getState());

    if (taskSession) {
      return lessonTaskApi.submitAnswersLessonTaskItem(
        `/task_sessions/${taskSession.id}/task_item_sessions/${taskItemSession.id}`,
        taskItemSession
      );
    }

    return null;
  }
);

const nextLessonTask = createAsyncThunk<
  void,
  {
    lessonSessionId: number;
    onNavigateToTask(params: { taskId: number }): void;
    onNavigateToResults(): void;
  },
  { dispatch: tSharedAppDispatch; state: tSharedAppState }
>(
  'lessonTaskSession/nextLessonTask_request',
  async ({ lessonSessionId, onNavigateToTask, onNavigateToResults }, { dispatch }) => {
    try {
      const { taskSession } = await lessonTaskApi.nextLessonTaskSession(lessonSessionId);
      onNavigateToTask({ taskId: taskSession.taskId });
    } catch (error) {
      if ((error as AxiosError).response?.status === 404) {
        // Running out of tasks is the server's lesson-completion path, which
        // is also when badges are granted — anything the member area fetched
        // for the celebration poster is stale from this moment on.
        dispatch(rewardNotificationsActions.invalidate());
        onNavigateToResults();
      } else {
        throw error;
      }
    }
  }
);

const finishLessonTask = createAsyncThunk<
  void,
  {
    taskSessionId: number;
    answers?: tAnswer;
  },
  { dispatch: tSharedAppDispatch; state: tSharedAppState }
>(
  'lessonTaskSession/finishLessonTask_request',
  async ({ taskSessionId, answers }, { dispatch, getState }) => {
    const taskSession = lessonTaskSelectors.selectLessonTaskSession(getState());

    if (taskSession?.id === taskSessionId) {
      if (answers) {
        await dispatch(
          submitAnswersLessonTask({
            taskSession,
            answers
          })
        );
      }
    }
  }
);

const finishAndNextLessonTask = createAsyncThunk<
  void,
  {
    lessonSessionId: number;
    taskSessionId: number;
    answers?: tAnswer;
    onNavigateToTask(params: { taskId: number }): void;
    onNavigateToResults(): void;
  },
  { dispatch: tSharedAppDispatch; state: tSharedAppState }
>(
  'lessonTaskSession/finishAndNextLessonTask_request',
  async (
    { lessonSessionId, taskSessionId, answers, onNavigateToTask, onNavigateToResults },
    { dispatch }
  ) => {
    await dispatch(finishLessonTask({ taskSessionId, answers }));

    await dispatch(
      nextLessonTask({ lessonSessionId, onNavigateToTask, onNavigateToResults })
    );
  }
);

const heartBeat = createAsyncThunk<void, void, { state: tSharedAppState }>(
  'lessonTaskSession/heartBeat_request',
  async (_, { getState }) => {
    const taskSession = lessonTaskSelectors.selectLessonTaskSession(getState());
    if (taskSession) {
      await lessonTaskApi.heartBeat(taskSession.url);
    }
  }
);

const getNextTaskSession = createAsyncThunk<
  | tPromiseFromFunctionResolved<
      typeof lessonTaskApi.nextLessonTaskSession
    >['taskSession']
  | null,
  void,
  { state: tSharedAppState }
>('lessonTaskSession/submitAnswersTask_request', async (_, { getState }) => {
  const lessonSessionId = getState().shared.lesson.session?.id;
  if (!lessonSessionId) return null;

  try {
    const { taskSession } = await lessonTaskApi.nextLessonTaskSession(lessonSessionId);
    return taskSession;
  } catch (_e) {
    return null;
  }
});

const lessonTaskSessionSlice = createSlice({
  name: 'lessonTaskSession',
  initialState: initialLessonTaskSessionState as tLessonTaskSession,
  reducers: {
    drop: () => initialLessonTaskSessionState
  },
  extraReducers: (builder) => {
    builder.addCase(fetchLessonTaskSession.pending, () => {
      return initialLessonTaskSessionState;
    });

    builder.addCase(fetchLessonTaskSession.fulfilled, (_, action) => {
      return {
        ...action.payload.taskSession,
        leaderBoard: action.payload?.leaderBoard || []
      };
    });

    builder.addCase(restartLessonTaskSession.fulfilled, (state, action) => {
      if (state) {
        return {
          ...state,
          ...action.payload?.taskSession
        };
      }

      return state;
    });

    builder.addCase(submitAnswersLessonTask.fulfilled, (state, action) => {
      if (state) {
        return {
          ...state,
          leaderBoard: action.payload?.leaderBoard || [],
          xp: action.payload?.taskSession.xp || 0
        };
      }

      return state;
    });
  }
});

const { reducer } = lessonTaskSessionSlice;

export const sessionActions = {
  fetchLessonTaskSession,
  restartLessonTaskSession,
  submitAnswersLessonTask,
  submitAnswersLessonTaskItem,
  nextLessonTask,
  finishLessonTask,
  finishAndNextLessonTask,
  heartBeat,
  getNextTaskSession
};

export default reducer;
export type { tLessonTaskSession };
